import {Chip} from "@/components/chip/Chip.tsx";
import {Incident as IncidentType, StatusPageLayoutPreset, StatusPageTimeFormat} from "@/lib/types/statuspages.ts";
import {isTwoColumnLayout} from "@/pages/statuspages/consumption/utils/layout.ts";
import classNames from "classnames";
import {formatInTimeZone} from "date-fns-tz";
import {parseISO} from "date-fns/parseISO";
import pluralize from "pluralize";
import {EmptyEventCard} from "../../shared/EmptyEventCard";
import {INCIDENTS_SORT_OPTIONS, SortOptions} from "../utils/sortIncidents";
import {IncidentItem} from "./IncidentItem";
import styles from "./IncidentsList.module.scss";
import {SortMenu} from "./SortMenu";

export interface IncidentsListProps {
  incidents: IncidentType[];
  title?: string;
  sortBy: SortOptions;
  onSortChange: (sortBy: SortOptions) => void;
  componentStatusRank: Record<string, number>;
  sortOptions?: Array<{value: SortOptions; label: string}>;
  emptyMessage?: string;
  emptyCtaLabel?: string;
  onEmptyCtaClick?: () => void;
  groupByMonth?: boolean;
  timeFormat?: StatusPageTimeFormat;
  timezone?: string;
  layout?: StatusPageLayoutPreset;
  variant?: "default" | "inspire";
}

export const IncidentsList = ({
  incidents,
  title,
  sortBy,
  onSortChange,
  componentStatusRank,
  sortOptions,
  emptyMessage,
  emptyCtaLabel,
  onEmptyCtaClick,
  groupByMonth = false,
  timeFormat,
  timezone,
  layout = StatusPageLayoutPreset.EXPANDED,
  variant = "default",
}: IncidentsListProps) => {
  const options = sortOptions ?? INCIDENTS_SORT_OPTIONS;
  const isTwoColumn = isTwoColumnLayout(layout);
  // Group headings must use the same timezone as each item's timestamp (IncidentItem defaults to UTC),
  // otherwise an incident near a month boundary can land under the wrong month heading.
  const tz = timezone ?? "UTC";

  const groupedIncidents = groupByMonth
    ? incidents.reduce<{label: string; key: string; items: IncidentType[]}[]>((acc, incident) => {
        const date = parseISO(incident.starts_at);
        const key = formatInTimeZone(date, tz, "yyyy-MM");
        const label = formatInTimeZone(date, tz, "MMMM yyyy");
        const existing = acc.find((g) => g.key === key);
        if (existing) {
          existing.items.push(incident);
        } else {
          acc.push({key, label, items: [incident]});
        }
        return acc;
      }, [])
    : null;

  return (
    <div className={styles.incidentsList}>
      <div className={`${styles.sectionHeader}${title ? "" : ` ${styles.sectionHeaderNoTitle}`}`}>
        {title ? (
          <div className={styles.titleRow}>
            <h4 className={styles.subsectionTitle}>{title}</h4>
            <Chip variant="count" size="tiny" aria-label={pluralize("Event", incidents.length, true)}>
              {incidents.length}
            </Chip>
          </div>
        ) : (
          <div>
            <div className={styles.componentCount}>{pluralize("Event", incidents.length, true)}</div>
          </div>
        )}
        <SortMenu sortBy={sortBy} onSortChange={onSortChange} options={options} variant={variant} />
      </div>
      <div className={classNames(styles.incidentsContainer, {[styles.compact]: isTwoColumn})}>
        {incidents.length > 0 ? (
          groupedIncidents ? (
            groupedIncidents.map((group) => (
              <div key={group.key} className={styles.monthGroup}>
                <div className={styles.monthHeader}>
                  {group.label} ({group.items.length})
                </div>
                <div className={styles.monthGroupItems}>
                  {group.items.map((incident) => (
                    <IncidentItem
                      key={incident.id}
                      incident={incident}
                      componentStatusRank={componentStatusRank}
                      timeFormat={timeFormat}
                      timezone={timezone}
                      layout={layout}
                      variant={variant}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            incidents.map((incident) => (
              <IncidentItem
                key={incident.id}
                incident={incident}
                componentStatusRank={componentStatusRank}
                timeFormat={timeFormat}
                timezone={timezone}
                layout={layout}
                variant={variant}
              />
            ))
          )
        ) : (
          <EmptyEventCard
            message={emptyMessage ?? "There are no events at this time."}
            ctaLabel={emptyCtaLabel}
            onCtaClick={onEmptyCtaClick}
          />
        )}
      </div>
    </div>
  );
};
